import { useEffect, useState } from "react";
import { api, getUser, clearAuth } from "../lib/apiClient";
import { useNavigate } from "react-router-dom";
import { Shield, LogOut, ArrowRight, ServerCrash, Crown, Bot, ExternalLink, UserCog } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Brand } from "../components/Brand";


export default function ServerPicker() {
  const [guilds, setGuilds] = useState([]);
  const [mode, setMode] = useState("demo");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFounder, setIsFounder] = useState(false);
  const user = getUser();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/guilds")
      .then(r => { setGuilds(r.data.guilds || []); setMode(r.data.mode || "demo"); setError(null); })
      .catch(e => setError(e?.response?.data?.detail?.discord_error?.message || "Failed to load servers"))
      .finally(() => setLoading(false));
    api.get("/auth/me").then(r => setIsFounder(!!r.data.is_founder)).catch(() => {});
  }, []);

  const logout = () => { clearAuth(); navigate("/"); };
  const select = (g) => {
    if (!g.bot_present) {
      toast.error("ModNode Bot is not in this server. Click INVITE BOT first.");
      return;
    }
    localStorage.setItem("mm_active_guild", JSON.stringify({ id: g.id, name: g.name, icon_url: g.icon_url }));
    navigate(`/dashboard/${g.id}`);
  };

  const inviteBot = async (g, e) => {
    e.stopPropagation();
    try {
      const r = await api.get(`/guilds/${g.id}/invite-url`);
      window.open(r.data.url, "_blank", "noopener,noreferrer");
      toast.success("Opened bot invite in a new tab. Come back and refresh once installed.");
    } catch {
      toast.error("Failed to create invite link");
    }
  };

  const withBot = guilds.filter(g => g.bot_present);
  const withoutBot = guilds.filter(g => !g.bot_present);

  return (
    <div className="min-h-screen bg-[#06070A] mm-grid-bg" data-testid="server-picker-page">
      
      {/* Top bar */}
      <div className="sticky top-0 z-20 border-b border-white/5 bg-[#06070A]/70 backdrop-blur-xl flex items-center justify-between px-6 md:px-10 py-4">
        <Brand sub="Select a server" />
        <div className="flex items-center gap-4">
          {isFounder && (
            <button
              data-testid="founder-console-btn"
              onClick={() => navigate("/owner")}
              className="flex items-center gap-2 px-4 py-2 bg-[#10B981] text-black font-mono text-[10px] tracking-widest font-bold hover:bg-[#10B981]/80 mm-glow-volt"
            >
              <Crown className="w-3.5 h-3.5" />
              FOUNDER CONSOLE
            </button>
          )}
          <div className="text-right">
            <div className="text-sm font-medium">{user?.global_name || user?.username || "Moderator"}</div>
            <div className="font-mono text-xs text-[#64748B]">{mode === "discord" ? "Discord" : "Signed in"}</div>
          </div>
          <button data-testid="logout-btn" onClick={logout} className="p-2 border border-white/10 hover:border-[#EF4444] hover:text-[#EF4444] transition-all">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-8 py-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-[#A78BFA] mb-4">Step 2 · Pick your server</div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Which server are we<br/><span className="mm-chrome italic">running today?</span>
            </h1>
            <p className="mt-4 text-[#94A3B8] max-w-2xl">
              {mode === "discord"
                ? <>Only servers where you're the <span className="text-white">owner</span> or hold <span className="text-white">MANAGE_GUILD</span> permission are shown. Servers missing the bot need to be invited first.</>
                : <>Pick a server the bot is in to open the mod console.</>
              }
            </p>
          </div>
          <div className="hidden md:flex flex-col items-end gap-1 font-mono text-xs text-[#64748B] tracking-widest">
            <span>{withBot.length} READY · {withoutBot.length} NEED BOT</span>
            <span className="text-[#10B981]">TOTAL: {guilds.length}</span>
          </div>
        </div>

        {loading && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-square border border-white/5 bg-[#0D0E15] animate-pulse" />
            ))}
          </div>
        )}

        {error && !loading && (
          <div className="border border-[#EF4444]/40 bg-[#EF4444]/5 p-8" data-testid="server-picker-error">
            <ServerCrash className="w-6 h-6 text-[#EF4444] mb-4" />
            <div className="font-mono text-xs tracking-widest text-[#EF4444] mb-2">ERROR / DISCORD API</div>
            <div className="text-white">{error}</div>
          </div>
        )}

        {!loading && !error && guilds.length === 0 && (
          <div className="border border-white/10 p-12 text-center">
            <div className="font-display font-bold text-xl mb-2">No servers found</div>
            <p className="text-[#94A3B8]">
              {mode === "discord"
                ? "You don't own or admin any Discord server. Create one or ask to be given the MANAGE_GUILD permission."
                : "Invite ModNode Bot to a Discord server first."}
            </p>
          </div>
        )}

        {/* READY servers (bot present) */}
        {withBot.length > 0 && (
          <>
            <div className="flex items-center gap-2 mt-4 mb-3">
              <span className="w-1.5 h-1.5 bg-[#10B981] pulse-dot" />
              <span className="font-mono text-[10px] tracking-widest text-[#10B981] uppercase">Ready · Bot Installed · {withBot.length}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {withBot.map((g, i) => (
                <ServerCard key={g.id} g={g} i={i} onClick={() => select(g)} />
              ))}
            </div>
          </>
        )}

        {/* NEED BOT servers */}
        {withoutBot.length > 0 && (
          <>
            <div className="flex items-center gap-2 mt-10 mb-3">
              <span className="w-1.5 h-1.5 bg-[#F59E0B] pulse-dot" />
              <span className="font-mono text-[10px] tracking-widest text-[#F59E0B] uppercase">Needs Bot Invite · {withoutBot.length}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {withoutBot.map((g, i) => (
                <ServerCard key={g.id} g={g} i={i} onInvite={(e) => inviteBot(g, e)} disabled />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ServerCard({ g, i, onClick, onInvite, disabled }) {
  return (
    <motion.div
      data-testid={`guild-card-${g.id}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.03 }}
      className={`relative border ${disabled ? "border-white/5" : "border-white/10 hover:border-[#8B5CF6] cursor-pointer"} bg-[#0D0E15] p-5 transition-all duration-200 ${disabled ? "opacity-70" : "hover:bg-[#151225]"}`}
      onClick={disabled ? undefined : onClick}
      whileHover={disabled ? undefined : { scale: 1.01 }}
    >
      <div className="flex items-center gap-4">
        {g.icon_url ? (
          <img src={g.icon_url} alt="" className={`w-14 h-14 rounded-md object-cover ${disabled ? "grayscale" : ""}`} />
        ) : (
          <div className="w-14 h-14 rounded-md bg-[#141622] flex items-center justify-center font-display font-bold text-lg text-[#8B5CF6]">
            {g.name?.split(" ").map(w=>w[0]).slice(0,2).join("").toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-sm truncate">{g.name}</div>
          <div className="font-mono text-[10px] text-[#64748B] mt-1 tracking-widest truncate">{g.id}</div>
          <div className="flex gap-1.5 mt-2 flex-wrap">
            {g.owner && (
              <span data-testid={`badge-owner-${g.id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#10B981]/10 border border-[#10B981]/40 text-[#10B981] font-mono text-[9px] tracking-widest">
                <Crown className="w-2.5 h-2.5" /> OWNER
              </span>
            )}
            {!g.owner && (
              <span data-testid={`badge-admin-${g.id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#8B5CF6]/10 border border-[#8B5CF6]/40 text-[#8B5CF6] font-mono text-[9px] tracking-widest">
                <UserCog className="w-2.5 h-2.5" /> ADMIN
              </span>
            )}
            {g.bot_present ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#10B981]/10 border border-[#10B981]/40 text-[#10B981] font-mono text-[9px] tracking-widest">
                <Bot className="w-2.5 h-2.5" /> BOT ONLINE
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#F59E0B]/10 border border-[#F59E0B]/40 text-[#F59E0B] font-mono text-[9px] tracking-widest">
                <Bot className="w-2.5 h-2.5" /> BOT MISSING
              </span>
            )}
          </div>
        </div>
        {disabled ? (
          <button
            data-testid={`invite-bot-${g.id}`}
            onClick={onInvite}
            className="shrink-0 px-3 py-2 bg-[#F59E0B] text-black font-mono text-[10px] tracking-widest font-bold hover:bg-[#F59E0B]/80 flex items-center gap-1.5"
          >
            INVITE <ExternalLink className="w-3 h-3" />
          </button>
        ) : (
          <ArrowRight className="w-4 h-4 text-[#64748B] shrink-0" />
        )}
      </div>
    </motion.div>
  );
}
